// Keyboard, game controller and on-screen controller (`touch.ts`) state → tank input. Offline two
// players share one keyboard and get the first and second controller; online either key set,
// every controller and the on-screen one work.
import { listenTouch, setTouchMode, touchInput } from './touch.js';

export interface TankInput {
  throttle: number; // -1, 0, 1
  turn: number; // -1, 0, 1
  fire: boolean;
  item: boolean; // use the power-up
}

interface KeyMap {
  up: string[];
  down: string[];
  left: string[];
  right: string[];
  fire: string[];
  item: string[];
}

export const KEYMAPS: KeyMap[] = [
  { up: ['KeyW'], down: ['KeyS'], left: ['KeyA'], right: ['KeyD'], fire: ['Space'], item: ['KeyE', 'ShiftLeft'] },
  {
    up: ['ArrowUp'],
    down: ['ArrowDown'],
    left: ['ArrowLeft'],
    right: ['ArrowRight'],
    fire: ['Enter', 'NumpadEnter'],
    // Many Mac keyboards have no right Ctrl: Option (Alt) right and Numpad 0 as well.
    item: ['ShiftRight', 'ControlRight', 'AltRight', 'Numpad0'],
  },
];

const GAME_KEYS = new Set(KEYMAPS.flatMap((m) => [...m.up, ...m.down, ...m.left, ...m.right, ...m.fire, ...m.item]));
const pressed = new Set<string>();
const MODIFIERS = ['Shift', 'Control', 'Alt', 'Meta'];

/**
 * The key's code; for modifiers from `location`, which knows the side where `code` may not (it
 * can come without a side or with the wrong one on macOS).
 */
function codeOf(e: KeyboardEvent): string {
  if (!MODIFIERS.includes(e.key) || e.location === KeyboardEvent.DOM_KEY_LOCATION_STANDARD) return e.code;
  return e.key + (e.location === KeyboardEvent.DOM_KEY_LOCATION_RIGHT ? 'Right' : 'Left');
}
const justPressed = new Set<string>();

/**
 * Controllers in the browser's standard layout (Xbox and PS5 alike): stick or D-pad drive, A/Cross
 * or RT/R2 fire, B/Circle or LT/L2 use the power-up, Start/Options presses the shop's button.
 * Digital like the keys: a stick counts from `STICK` on (Wunsch User 2026-10-26).
 */
const STICK = 0.5;
const PAD = { a: 0, b: 1, x: 2, y: 3, lt: 6, rt: 7, start: 9, up: 12, down: 13, left: 14, right: 15 };
const NO_INPUT: TankInput = { throttle: 0, turn: 0, fire: false, item: false };
/** Per connected controller in order: its input this frame. */
let pads: TankInput[] = [];
/** Per button with listeners: the pads holding it down, and the listeners of its press. */
const padPresses = new Map<number, { down: Set<number>; listeners: (() => void)[] }>();
/** The pads of `pads`, for the rumble. */
let padDevices: Gamepad[] = [];
/** Online all controllers steer the one tank, so all rumble for it. */
let shared = false;

/** `fn` on each press of a controller's Start/Options button. */
export function onPadStart(fn: () => void): void {
  onPadPress(PAD.start, fn);
}

/** `fn` on each press of a controller's Y/△ (`which` 'y') or X/□ ('x') button: the emotes. */
export function onPadEmote(which: 'x' | 'y', fn: () => void): void {
  onPadPress(PAD[which], fn);
}

function onPadPress(button: number, fn: () => void): void {
  const entry = padPresses.get(button) ?? { down: new Set<number>(), listeners: [] };
  entry.listeners.push(fn);
  padPresses.set(button, entry);
}

function pollPads(): void {
  const down = (p: Gamepad, b: number) => p.buttons[b]?.pressed ?? false;
  padDevices = navigator.getGamepads().filter((p): p is Gamepad => !!p?.connected);
  pads = padDevices.map((p) => {
    const [x, y] = [p.axes[0] ?? 0, p.axes[1] ?? 0];
    const input = {
      throttle: (down(p, PAD.up) || y <= -STICK ? 1 : 0) - (down(p, PAD.down) || y >= STICK ? 1 : 0),
      turn: (down(p, PAD.right) || x >= STICK ? 1 : 0) - (down(p, PAD.left) || x <= -STICK ? 1 : 0),
      fire: down(p, PAD.a) || down(p, PAD.rt),
      item: down(p, PAD.b) || down(p, PAD.lt),
    };
    if (input.throttle || input.turn || input.fire || input.item) {
      document.body.classList.add('no-cursor');
      setTouchMode(false);
    }
    for (const [button, entry] of padPresses) {
      if (!down(p, button)) entry.down.delete(p.index);
      else if (!entry.down.has(p.index)) {
        entry.down.add(p.index);
        for (const fn of entry.listeners) fn();
      }
    }
    return input;
  });
  requestAnimationFrame(pollPads);
}

/** A short shake of the controllers steering tank `tank` (`strong` when it dies). */
export function rumble(tank: number, strong: boolean): void {
  for (const [i, p] of padDevices.entries()) {
    if (!shared && i !== tank) continue;
    void p.vibrationActuator?.playEffect('dual-rumble', strong ? { duration: 350, strongMagnitude: 1, weakMagnitude: 0.6 } : { duration: 120, strongMagnitude: 0.3, weakMagnitude: 0.7 }).catch(() => {});
  }
}

export function listenInput(): void {
  listenTouch();
  if ('getGamepads' in navigator) requestAnimationFrame(pollPads);
  window.addEventListener('keydown', (e) => {
    const t = e.target;
    if (t instanceof HTMLInputElement || t instanceof HTMLSelectElement || t instanceof HTMLTextAreaElement) return;
    // A clicked button keeps focus; Space/Enter would press it again instead of firing.
    if (t instanceof HTMLButtonElement) t.blur();
    const code = codeOf(e);
    if (GAME_KEYS.has(code)) {
      e.preventDefault();
      document.body.classList.add('no-cursor'); // playing: the pointer only gets in the way
      setTouchMode(false);
    }
    if (!e.repeat) justPressed.add(code);
    pressed.add(code);
  });
  window.addEventListener('keyup', (e) => {
    pressed.delete(codeOf(e));
    pressed.delete(e.code);
  });
  window.addEventListener('blur', () => pressed.clear());
  // Back as soon as the mouse really moves (browsers also send moves without one, e.g. when
  // the page changes under a still pointer).
  let last = [NaN, NaN];
  window.addEventListener('mousemove', (e) => {
    if (e.screenX === last[0] && e.screenY === last[1]) return;
    last = [e.screenX, e.screenY];
    document.body.classList.remove('no-cursor');
  });
}

/** True once per physical key press. */
export function takePress(code: string): boolean {
  return justPressed.delete(code);
}

const any = (codes: string[]) => codes.some((c) => pressed.has(c));

function keyInput(player: number): TankInput {
  const m = KEYMAPS[player];
  if (!m) return NO_INPUT;
  return {
    throttle: (any(m.up) ? 1 : 0) - (any(m.down) ? 1 : 0),
    turn: (any(m.right) ? 1 : 0) - (any(m.left) ? 1 : 0),
    fire: any(m.fire),
    item: any(m.item),
  };
}

/** Offline player `player`: its key set and its controller. */
export function readInput(player: number): TankInput {
  shared = false;
  return merge([keyInput(player), pads[player] ?? NO_INPUT]);
}

/** Online: one player, both key sets, all controllers and the on-screen one. */
export function readAnyInput(): TankInput {
  shared = true;
  return merge([...KEYMAPS.map((_, i) => keyInput(i)), ...pads, touchInput()]);
}

function merge(inputs: TankInput[]): TankInput {
  const clamp = (v: number) => Math.max(-1, Math.min(1, v));
  return {
    throttle: clamp(inputs.reduce((sum, i) => sum + i.throttle, 0)),
    turn: clamp(inputs.reduce((sum, i) => sum + i.turn, 0)),
    fire: inputs.some((i) => i.fire),
    item: inputs.some((i) => i.item),
  };
}
